import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import { 
  Users, 
  CreditCard, 
  TrendingUp, 
  Activity,
  ArrowLeft,
  DollarSign,
  Calendar,
  Clock,
  Target,
  Award,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  BarChart3,
  PieChart,
  LineChart,
  Globe
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  fetchClients,
  Client,
  getActiveClients,
  getExpiringClients,
  getExpiredClients,
  getActiveRate,
  getAverageProgress,
  getMonthlyRevenue,
  getYearlyRevenue,
  getDaysUntilExpiration,
  calculateClientProgress,
  getClientStatus,
  getPaidClients,
  getUnpaidClients
} from '@/services/clientService';
import { mockClients } from '@/data/clientsData';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  LineChart as RechartsLineChart,
  Line
} from 'recharts';
import WebsiteTrafficSection from '@/components/admin/WebsiteTrafficSection';
import { useWebsiteAnalytics, type DatePreset } from '@/hooks/useWebsiteAnalytics';

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading, signOut } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  
  // Website analytics hook
  const { analytics: websiteAnalytics, loading: analyticsLoading, error: analyticsError, refetch: refetchAnalytics, dateRange: analyticsDates, preset: analyticsPreset, setPreset: setAnalyticsPreset } = useWebsiteAnalytics();

  // Removed custom cursor for light theme

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    }
  }, [user, role, authLoading, navigate]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const data = await fetchClients();
      if (data.length === 0) {
        setUseMockData(true);
        const convertedMockData: Client[] = mockClients.map((m) => ({
          id: m.id.toString(),
          user_id: null,
          name: m.name,
          email: m.email,
          plan: m.plan,
          plan_price: m.planPrice,
          status: m.status,
          paid: m.status !== 'expired',
          subscription_start: m.subscriptionStart,
          subscription_end: m.subscriptionEnd,
          last_active_at: m.lastActiveDate.toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          academy_progress: m.academyProgress.map((ap) => ({
            id: `${m.id}-${ap.academyId}`,
            client_id: m.id.toString(),
            academy_id: ap.academyId,
            academy_name: ap.academyName,
            completed_modules: ap.completedModules,
            total_modules: ap.totalModules,
          })),
        }));
        setClients(convertedMockData);
      } else {
        setUseMockData(false);
        setClients(data);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setUseMockData(true);
      const convertedMockData: Client[] = mockClients.map((m) => ({
        id: m.id.toString(),
        user_id: null,
        name: m.name,
        email: m.email,
        plan: m.plan,
        plan_price: m.planPrice,
        status: m.status,
        paid: m.status !== 'expired',
        subscription_start: m.subscriptionStart,
        subscription_end: m.subscriptionEnd,
        last_active_at: m.lastActiveDate.toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        academy_progress: m.academyProgress.map((ap) => ({
          id: `${m.id}-${ap.academyId}`,
          client_id: m.id.toString(),
          academy_id: ap.academyId,
          academy_name: ap.academyName,
          completed_modules: ap.completedModules,
          total_modules: ap.totalModules,
        })),
      }));
      setClients(convertedMockData);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user && role === 'admin') {
      loadClients();
    }
  }, [user, role]);

  // Calculations
  const totalClients = clients.length;
  const activeClients = getActiveClients(clients);
  const expiringClients = getExpiringClients(clients);
  const expiredClients = getExpiredClients(clients);
  const paidClients = getPaidClients(clients);
  const unpaidClients = getUnpaidClients(clients);
  const activeRate = getActiveRate(clients);
  const averageProgress = getAverageProgress(clients);
  const monthlyRevenue = getMonthlyRevenue(clients);
  const yearlyRevenue = getYearlyRevenue(clients);
  const totalContractValue = activeClients.reduce((acc, c) => acc + c.plan_price, 0);

  // Revenue by plan type
  const annualPlanClients = clients.filter(c => c.plan === 'Annual');
  const monthlyPlanClients = clients.filter(c => c.plan === 'Monthly');

  // Status distribution for pie chart
  const statusData = [
    { name: 'Active', value: activeClients.length, color: '#22c55e' },
    { name: 'Expiring', value: expiringClients.length, color: '#eab308' },
    { name: 'Expired', value: expiredClients.length, color: '#ef4444' },
  ];

  // Payment status distribution
  const paymentData = [
    { name: 'Paid', value: paidClients.length, color: '#22c55e' },
    { name: 'Unpaid', value: unpaidClients.length, color: '#ef4444' },
  ];

  // Plan distribution
  const planData = [
    { name: 'Annual', value: annualPlanClients.length, revenue: annualPlanClients.reduce((acc, c) => acc + c.plan_price, 0) },
    { name: 'Monthly', value: monthlyPlanClients.length, revenue: monthlyPlanClients.reduce((acc, c) => acc + c.plan_price, 0) },
  ];

  // Academy progress breakdown
  const academyProgressData = (() => {
    const academyMap: Record<string, { name: string; totalCompleted: number; totalModules: number; clientCount: number }> = {};
    
    clients.forEach(client => {
      client.academy_progress?.forEach(progress => {
        if (!academyMap[progress.academy_id]) {
          academyMap[progress.academy_id] = {
            name: progress.academy_name,
            totalCompleted: 0,
            totalModules: 0,
            clientCount: 0,
          };
        }
        academyMap[progress.academy_id].totalCompleted += progress.completed_modules;
        academyMap[progress.academy_id].totalModules += progress.total_modules;
        academyMap[progress.academy_id].clientCount += 1;
      });
    });

    return Object.values(academyMap).map(a => ({
      name: a.name.split(' ').slice(0, 2).join(' '),
      fullName: a.name,
      avgProgress: a.totalModules > 0 ? Math.round((a.totalCompleted / a.totalModules) * 100) : 0,
      completionRate: a.clientCount > 0 ? Math.round((a.totalCompleted / a.clientCount / 10) * 100) : 0,
    }));
  })();

  // Client progress distribution
  const progressDistribution = (() => {
    const ranges = [
      { range: '0-20%', min: 0, max: 20, count: 0 },
      { range: '21-40%', min: 21, max: 40, count: 0 },
      { range: '41-60%', min: 41, max: 60, count: 0 },
      { range: '61-80%', min: 61, max: 80, count: 0 },
      { range: '81-100%', min: 81, max: 100, count: 0 },
    ];

    clients.forEach(client => {
      const progress = calculateClientProgress(client);
      const range = ranges.find(r => progress >= r.min && progress <= r.max);
      if (range) range.count += 1;
    });

    return ranges;
  })();

  // Monthly revenue simulation (last 6 months)
  const revenueHistory = (() => {
    const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const baseRevenue = monthlyRevenue;
    return months.map((month, i) => ({
      month,
      revenue: Math.round(baseRevenue * (0.7 + (i * 0.06) + Math.random() * 0.1)),
      projected: Math.round(baseRevenue * (1 + (i * 0.05))),
    }));
  })();

  // Top performing clients
  const topClients = [...clients]
    .map(c => ({ ...c, progress: calculateClientProgress(c) }))
    .sort((a, b) => b.progress - a.progress)
    .slice(0, 5);

  // Clients needing attention (expiring soon, low progress, unpaid)
  const attentionClients = clients
    .filter(c => {
      const days = getDaysUntilExpiration(c.subscription_end);
      const progress = calculateClientProgress(c);
      return (days > 0 && days <= 30) || progress < 20 || !c.paid;
    })
    .slice(0, 5);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-border bg-white/80 backdrop-blur-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <span className="text-lg font-semibold text-foreground">
            Faceless Aura <span className="text-primary">Club</span>
          </span>
          <div className="h-6 w-px bg-border" />
          <span className="text-sm font-medium text-primary">Analytics Dashboard</span>
          {useMockData && (
            <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
              Demo Data
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-muted-foreground hover:text-foreground"
            onClick={() => {
              loadClients();
              refetchAnalytics();
            }}
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          <span className="text-sm text-muted-foreground">{user?.email}</span>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-muted-foreground hover:text-primary" 
            onClick={signOut}
          >
            Sign Out
          </Button>
        </div>
      </motion.header>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h1 className="text-2xl md:text-3xl font-light tracking-wide mb-2">
            <span className="text-foreground">Analytics & Insights</span>
          </h1>
          <p className="text-muted-foreground text-sm">
            Comprehensive overview of platform performance and client engagement
          </p>
        </motion.div>

        {/* Website Traffic Section */}
        <WebsiteTrafficSection
          analytics={websiteAnalytics}
          loading={analyticsLoading}
          error={analyticsError}
          preset={analyticsPreset}
          onPresetChange={setAnalyticsPreset}
          onRefresh={refetchAnalytics}
          dateRange={analyticsDates}
        />

        {/* Client Metrics Header */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-light text-foreground">Client Analytics</h2>
          </div>
          <p className="text-sm text-muted-foreground">Member engagement and revenue metrics</p>
        </motion.div>

        {/* Key Metrics */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {[
            { label: 'Total Clients', value: totalClients, icon: Users, color: 'text-primary' },
            { label: 'Active', value: activeClients.length, icon: CheckCircle, color: 'text-green-400' },
            { label: 'Expiring', value: expiringClients.length, icon: AlertTriangle, color: 'text-yellow-400' },
            { label: 'Expired', value: expiredClients.length, icon: XCircle, color: 'text-red-400' },
            { label: 'Avg Progress', value: `${averageProgress}%`, icon: Target, color: 'text-blue-400' },
            { label: 'Active Rate', value: `${activeRate}%`, icon: Activity, color: 'text-primary' },
          ].map((stat, index) => (
            <div
              key={index}
              className="p-4 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg"
            >
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</span>
              </div>
              <p className="text-2xl font-light text-foreground">{stat.value}</p>
            </div>
          ))}
        </motion.div>

        {/* Revenue Overview */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {/* Monthly Revenue */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-green-400" />
              <h3 className="text-sm font-medium text-foreground">Monthly Revenue</h3>
            </div>
            <p className="text-3xl font-light text-foreground mb-2">
              ${Math.round(monthlyRevenue).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Payments this month</p>
          </div>

          {/* Yearly Revenue */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Yearly Revenue</h3>
            </div>
            <p className="text-3xl font-light text-foreground mb-2">
              ${Math.round(yearlyRevenue).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Jan 1 - Today</p>
          </div>

          {/* Unpaid Revenue */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <h3 className="text-sm font-medium text-foreground">Unpaid Clients</h3>
            </div>
            <p className="text-3xl font-light text-foreground mb-2">
              {unpaidClients.length}
            </p>
            <p className="text-xs text-muted-foreground">
              ${unpaidClients.reduce((acc, c) => acc + c.plan_price, 0).toLocaleString()} outstanding
            </p>
          </div>
        </motion.div>

        {/* Charts Row 1 */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {/* Revenue Trend */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <LineChart className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Revenue Trend (Last 6 Months)</h3>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueHistory}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="hsl(var(--primary))"
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Distribution */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <PieChart className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Client Status Distribution</h3>
            </div>
            <div className="h-64 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend
                    formatter={(value) => <span className="text-muted-foreground text-xs">{value}</span>}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Charts Row 2 */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          {/* Academy Progress */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Academy Completion Rates</h3>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={academyProgressData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[0, 100]} />
                  <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={10} width={80} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`${value}%`, 'Avg Progress']}
                  />
                  <Bar dataKey="avgProgress" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Progress Distribution */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <Target className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Client Progress Distribution</h3>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={progressDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis dataKey="range" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [`${value} clients`, 'Count']}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Plan & Payment Stats */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {/* Plan Distribution */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <CreditCard className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Plan Distribution</h3>
            </div>
            <div className="space-y-4">
              {planData.map((plan, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-background/50 rounded-lg border border-border/20">
                  <div>
                    <p className="text-sm font-medium text-foreground">{plan.name} Plan</p>
                    <p className="text-xs text-muted-foreground">{plan.value} clients</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-light text-foreground">${plan.revenue.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total revenue</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Status */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <DollarSign className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Payment Status</h3>
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={paymentData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {paymentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend
                    formatter={(value) => <span className="text-muted-foreground text-xs">{value}</span>}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Lists Row */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          {/* Top Performers */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <Award className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Top Performing Clients</h3>
            </div>
            <div className="space-y-3">
              {topClients.map((client, index) => (
                <div key={client.id} className="flex items-center justify-between p-3 bg-background/50 rounded-lg border border-border/20">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-medium">
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground">{client.name}</p>
                      <p className="text-xs text-muted-foreground">{client.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-muted/20 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-primary/60 to-primary rounded-full"
                        style={{ width: `${client.progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-8">{client.progress}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Clients Needing Attention */}
          <div className="p-6 border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <h3 className="text-sm font-medium text-foreground">Clients Needing Attention</h3>
            </div>
            <div className="space-y-3">
              {attentionClients.length > 0 ? attentionClients.map((client) => {
                const days = getDaysUntilExpiration(client.subscription_end);
                const progress = calculateClientProgress(client);
                const reasons: string[] = [];
                if (days > 0 && days <= 30) reasons.push(`Expires in ${days} days`);
                if (progress < 20) reasons.push(`Low progress (${progress}%)`);
                if (!client.paid) reasons.push('Unpaid');

                return (
                  <div key={client.id} className="flex items-center justify-between p-3 bg-background/50 rounded-lg border border-yellow-500/20">
                    <div>
                      <p className="text-sm font-medium text-foreground">{client.name}</p>
                      <p className="text-xs text-muted-foreground">{client.email}</p>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                      {reasons.map((reason, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              }) : (
                <p className="text-center text-muted-foreground text-sm py-8">All clients are in good standing!</p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
